import React from 'react'
import {
    Container,
    Row, Col, Label,
    Input, Button, Table, FormGroup,
} from 'reactstrap'
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io'
import {
    customQuery,
    getSavedQueries,
    saveQuery,
    updateSavedQuery,
    deleteSavedQuery,
} from '../model/request/custom_sql.js'
import { AlertModalModel } from './modal/AlertModalController'
import {
    ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ReferenceLine, ResponsiveContainer,
    PieChart, Pie, Cell,
} from 'recharts'

// ─── Chart helper functions ───────────────────────────────────────────────────

const PIE_COLORS = [
    '#4e79a7', '#f28e2b', '#e15759', '#76b7b2',
    '#59a14f', '#edc948', '#b07aa1', '#ff9da7',
    '#9c755f', '#bab0ac', '#d37295', '#a0cbe8',
]

function isNumericVal(val) {
    if (val === null || val === '' || val === undefined) return false
    return !isNaN(Number(val))
}

function isDateVal(val) {
    if (val instanceof Date) return true
    if (val && typeof val === 'object' && typeof val.toDate === 'function') return true
    if (typeof val === 'string') return /^\d{4}-\d{2}-\d{2}/.test(val)
    return false
}

function toNumericX(val) {
    if (val instanceof Date) return val.getTime()
    if (val && typeof val === 'object' && typeof val.toDate === 'function') return val.toDate().getTime()
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) return new Date(val).getTime()
    return Number(val)
}

function detectChartType(results) {
    if (!results || results.length < 2) return null
    const cols = Object.keys(results[0])
    if (cols.length < 2) return null
    const xCol = cols[0], yCol = cols[1]
    const hasNumericY = results.slice(0, 5).some(r => !isNaN(parseYValue(r[yCol])))
    if (!hasNumericY) return null
    const xSample = results[0][xCol]
    if (isNumericVal(xSample) || isDateVal(xSample)) {
        return { type: 'numeric', xCol, yCol, isDate: isDateVal(xSample) }
    }
    return { type: 'pie', xCol, yCol }
}

function quadraticRegression(points) {
    // Solve least-squares normal equations for y = ax² + bx + c
    const n = points.length
    let sx = 0, sx2 = 0, sx3 = 0, sx4 = 0, sy = 0, sxy = 0, sx2y = 0
    for (const { x, y } of points) {
        sx += x; sx2 += x * x; sx3 += x * x * x; sx4 += x * x * x * x
        sy += y; sxy += x * y; sx2y += x * x * y
    }
    // 3×3 system via Gaussian elimination
    let A = [
        [sx4, sx3, sx2, sx2y],
        [sx3, sx2, sx,  sxy ],
        [sx2, sx,  n,   sy  ],
    ]
    for (let col = 0; col < 3; col++) {
        let maxRow = col
        for (let row = col + 1; row < 3; row++) {
            if (Math.abs(A[row][col]) > Math.abs(A[maxRow][col])) maxRow = row
        }
        ;[A[col], A[maxRow]] = [A[maxRow], A[col]]
        for (let row = col + 1; row < 3; row++) {
            const f = A[row][col] / A[col][col]
            for (let j = col; j <= 3; j++) A[row][j] -= f * A[col][j]
        }
    }
    const c = A[2][3] / A[2][2]
    const b = (A[1][3] - A[1][2] * c) / A[1][1]
    const a = (A[0][3] - A[0][2] * c - A[0][1] * b) / A[0][0]
    return { a, b, c }
}

function linearRegression(points) {
    const [p1, p2] = points
    if (p2.x === p1.x) return { a: 0, b: 0, c: (p1.y + p2.y) / 2 }
    const m = (p2.y - p1.y) / (p2.x - p1.x)
    return { a: 0, b: m, c: p1.y - m * p1.x }
}

function evalReg({ a, b, c }, x) {
    return a * x * x + b * x + c
}

function buildChartData(results, xCol, yCol, isDate) {
    const points = results
        .map(r => ({ x: toNumericX(r[xCol]), y: parseYValue(r[yCol]) }))
        .filter(p => !isNaN(p.x) && !isNaN(p.y))
        .sort((a, b) => a.x - b.x)

    if (points.length < 2) return { chartData: [], avg: 0 }

    const reg = points.length === 2 ? linearRegression(points) : quadraticRegression(points)

    const xMin = points[0].x
    const xMax = points[points.length - 1].x
    const range = xMax - xMin
    const extendBy = Math.ceil(range * 0.20) || 1
    const predMax = xMax + extendBy

    const avg = points.reduce((s, p) => s + p.y, 0) / points.length

    // Actual data rows
    const actualRows = points.map(p => ({
        x: p.x,
        xLabel: isDate ? new Date(p.x).toLocaleDateString() : p.x,
        y: p.y,
        pred: Math.round(evalReg(reg, p.x) * 100) / 100,
    }))

    // Prediction-only rows (extend beyond data)
    const predCount = points.length === 2 ? 1 : 20
    const predRows = []
    for (let i = 1; i <= predCount; i++) {
        const x = xMax + (extendBy / predCount) * i
        predRows.push({
            x,
            xLabel: isDate ? new Date(x).toLocaleDateString() : Math.round(x * 100) / 100,
            y: null,
            pred: Math.round(evalReg(reg, x) * 100) / 100,
        })
    }

    return { chartData: [...actualRows, ...predRows], avg }
}

function parseYValue(val) {
    if (val === null || val === undefined) return NaN
    const n = Number(val)
    if (!isNaN(n)) return n
    const m = String(val).match(/^([\d.]+)\s*(GB|MB|kB|KB|bytes?|B)$/i)
    if (m) {
        const num = parseFloat(m[1])
        const unit = m[2].toLowerCase()
        if (unit === 'gb') return num * 1073741824
        if (unit === 'mb') return num * 1048576
        if (unit === 'kb') return num * 1024
        return num
    }
    return NaN
}

function isJsonValue(val) {
    return val !== null && typeof val === 'object'
}

// ─── Controller ───────────────────────────────────────────────────────────────

class CustomSQLViewController extends React.Component {

    constructor(props) {
        super(props)
        this.state = {
            sql: 'SELECT table_name FROM information_schema.tables WHERE table_schema=\'public\' AND table_type=\'BASE TABLE\'',
            results: [],
            savedQueries: [],
            queryName: '',
            currentQueryId: null,
            expandedCell: null, // { rowIndex, colName }
            showSavedQueries: true,
        }
        this.runSql = this.runSql.bind(this)
        this.handleSave = this.handleSave.bind(this)
    }

    componentDidMount() {
        this.loadSavedQueries()
    }

    loadSavedQueries() {
        this.props.turnOnLoader()
        getSavedQueries().then(
            (response) => {
                this.setState({ savedQueries: response.data.queries })
                this.props.turnOffLoader()
            },
            (error) => {
                this.props.showError(error)
                this.props.turnOffLoader()
            },
        )
    }

    runSql() {
        this.props.turnOnLoader()
        customQuery(this.state.sql).then(
            (response) => {
                const results = response.data.results
                this.setState({ results: (results && results.rows) || [], expandedCell: null })
                this.props.turnOffLoader()
            },
            (error) => {
                this.props.showError(error)
                this.props.turnOffLoader()
            }
        )
    }

    handleSave() {
        const { sql, queryName, currentQueryId } = this.state
        if (!queryName.trim()) {
            alert('Please enter a name for this query.')
            return
        }
        this.props.turnOnLoader()
        const action = currentQueryId
            ? updateSavedQuery(currentQueryId, queryName.trim(), sql)
            : saveQuery(queryName.trim(), sql)
        action.then(
            (response) => {
                if (!currentQueryId && response.data.saved) {
                    this.setState({ currentQueryId: response.data.saved.id })
                }
                this.loadSavedQueries()
                this.props.turnOffLoader()
            },
            (error) => {
                this.props.showError(error)
                this.props.turnOffLoader()
            },
        )
    }

    handleDelete(id) {
        this.props.turnOnLoader()
        deleteSavedQuery(id).then(
            () => {
                if (this.state.currentQueryId === id) {
                    this.setState({ currentQueryId: null, queryName: '' })
                }
                this.loadSavedQueries()
            },
            (error) => {
                this.props.showError(error)
                this.props.turnOffLoader()
            },
        )
    }

    handleLoad(q) {
        this.setState({ sql: q.sql, queryName: q.name, currentQueryId: q.id, results: [] })
    }

    toggleCell(rowIndex, colName) {
        const { expandedCell } = this.state
        if (expandedCell && expandedCell.rowIndex === rowIndex && expandedCell.colName === colName) {
            this.setState({ expandedCell: null })
        } else {
            this.setState({ expandedCell: { rowIndex, colName } })
        }
    }

    renderPieChart(results, xCol, yCol) {
        const MAX_PIE_SLICES = 8
        const allData = results.map(r => ({
            name: String(r[xCol]),
            value: parseYValue(r[yCol]),
            displayValue: String(r[yCol]),
        })).filter(d => !isNaN(d.value))
        const data = allData.slice(0, MAX_PIE_SLICES)
        const truncated = allData.length > MAX_PIE_SLICES

        if (data.length === 0) return null

        return (
            <div style={{ marginTop: '24px' }}>
                <h6>{xCol} / {yCol}</h6>
                <ResponsiveContainer width="100%" height={400}>
                    <PieChart>
                        <Pie
                            data={data}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={150}
                            label={({ name, payload }) => `${name}: ${payload.displayValue}`}
                        >
                            {data.map((_, i) => (
                                <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip formatter={(value, name, props) => [props.payload.displayValue || value, name]} />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
                {truncated && <p style={{ fontSize: '0.8em', opacity: 0.6, marginTop: '4px' }}>Showing first {MAX_PIE_SLICES} of {allData.length} categories</p>}
            </div>
        )
    }

    renderNumericChart(results, xCol, yCol, isDate) {
        const { chartData, avg } = buildChartData(results, xCol, yCol, isDate)
        if (chartData.length === 0) return null

        const avgRounded = Math.round(avg * 100) / 100

        return (
            <div style={{ marginTop: '24px' }}>
                <h6>{xCol} / {yCol}</h6>
                <ResponsiveContainer width="100%" height={400}>
                    <ComposedChart data={chartData} margin={{ top: 8, right: 32, left: 16, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                        <XAxis dataKey="xLabel" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip
                            formatter={(value, name) => {
                                if (name === 'pred') return [value, 'Regression / Prediction']
                                if (name === 'y') return [value, yCol]
                                return [value, name]
                            }}
                            labelFormatter={(label) => `${xCol}: ${label}`}
                        />
                        <Legend
                            formatter={(value) => {
                                if (value === 'pred') return 'Regression / Prediction'
                                if (value === 'y') return yCol
                                return value
                            }}
                        />
                        <ReferenceLine y={avgRounded} stroke="#f28e2b" strokeDasharray="6 3" label={{ value: `avg ${avgRounded}`, fill: '#f28e2b', fontSize: 11 }} />
                        <Bar dataKey="y" fill="#4e79a7" opacity={0.75} name="y" />
                        <Line dataKey="pred" type="monotone" stroke="#e15759" strokeWidth={2} strokeDasharray="5 3" dot={false} name="pred" />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        )
    }

    renderChart() {
        const { results } = this.state
        const info = detectChartType(results)
        if (!info || info.type === 'none') return null
        if (info.type === 'pie') return this.renderPieChart(results, info.xCol, info.yCol)
        if (info.type === 'numeric') return this.renderNumericChart(results, info.xCol, info.yCol, info.isDate)
        return null
    }

    renderResults() {
        const { results, expandedCell } = this.state
        if (!results || results.length === 0) return null
        const cols = Object.keys(results[0])
        return (
            <Row><Col><div style={{ overflowX: 'auto' }}><Table>
                <thead>
                    <tr>{cols.map(c => <th key={c}>{c}</th>)}</tr>
                </thead>
                <tbody>
                    {results.map((row, i) => {
                        const isExpanded = expandedCell && expandedCell.rowIndex === i
                        const expandedVal = isExpanded ? row[expandedCell.colName] : null
                        const pretty = isExpanded ? JSON.stringify(expandedVal, null, 2) : null
                        return (
                            <React.Fragment key={i}>
                                <tr>
                                    {cols.map(c => {
                                        const val = row[c]
                                        const active = isExpanded && expandedCell.colName === c
                                        if (isJsonValue(val)) {
                                            return (
                                                <td key={c}>
                                                    <Button
                                                        color="secondary"
                                                        onClick={() => this.toggleCell(i, c)}
                                                    >
                                                        {active
                                                            ? <IoIosArrowUp style={{ fontWeight: '900' }} />
                                                            : <IoIosArrowDown style={{ fontWeight: '900' }} />
                                                        }
                                                    </Button>
                                                </td>
                                            )
                                        }
                                        return <td key={c}>{String(val ?? '')}</td>
                                    })}
                                </tr>
                                {isExpanded && (
                                    <tr>
                                        <td colSpan={cols.length} style={{ padding: '0 12px 12px 12px', borderTop: 'none' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                                <span style={{ fontSize: '0.8em', opacity: 0.6 }}>{expandedCell.colName}</span>
                                                <Button
                                                    size="sm"
                                                    style={{ fontSize: '0.75em' }}
                                                    onClick={() => navigator.clipboard.writeText(pretty)}
                                                >
                                                    Copy
                                                </Button>
                                            </div>
                                            <pre style={{
                                                background: 'rgba(128,128,128,0.08)',
                                                borderRadius: '4px',
                                                padding: '8px',
                                                fontSize: '0.78em',
                                                maxHeight: '300px',
                                                overflowY: 'auto',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                                marginBottom: 0,
                                            }}>{pretty}</pre>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        )
                    })}
                </tbody>
            </Table></div></Col></Row>
        )
    }

    renderSavedQueries() {
        const { savedQueries, showSavedQueries } = this.state
        if (savedQueries.length === 0) return null
        return (
            <>
                <h6
                    style={{ marginTop: '24px', cursor: 'pointer', userSelect: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                    onClick={() => this.setState({ showSavedQueries: !showSavedQueries })}
                >
                    Saved Queries {showSavedQueries ? <IoIosArrowUp /> : <IoIosArrowDown />}
                </h6>
                {showSavedQueries && <Row><Col><div style={{ overflowX: 'auto' }}><Table size="sm">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {savedQueries.map((q) => (
                            <tr key={q.id}>
                                <td>{q.name}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                    <Button size="sm" style={{ marginRight: '6px' }} onClick={() => this.handleLoad(q)}>
                                        Load
                                    </Button>
                                    <Button size="sm" color="danger" onClick={() => {
                                        this.props.showAlertModal(
                                            (new AlertModalModel())
                                                .setTitle('Warning')
                                                .setMessage(`Are you sure you want to delete "${q.name}"? This action cannot be undone.`)
                                                .addYesButton(() => {
                                                    this.handleDelete(q.id)
                                                    this.props.closeModal()
                                                })
                                                .addCancelButton(() => this.props.closeModal())
                                        )
                                    }}>
                                        Delete
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table></div></Col></Row>}
            </>
        )
    }

    render() {
        const { sql, queryName, currentQueryId, results } = this.state
        return (
            <Container>
                <Row><Col><Label style={{ fontSize: '20pt', fontWeight: '400' }}>SQL</Label></Col></Row>
                <Row>
                    <Input
                        type="textarea"
                        rows={5}
                        value={sql}
                        onChange={(e) => this.setState({ sql: e.target.value })}
                    />
                </Row>
                <Row style={{ marginTop: '8px' }} className="align-items-center g-2">
                    <Col xs="auto">
                        <Button onClick={this.runSql}>Query</Button>
                    </Col>
                    {results.length > 0 && (
                        <Col xs="auto">
                            <Button
                                className="btn-copy"
                                onClick={() => navigator.clipboard.writeText(JSON.stringify(results, null, 2))}
                            >Copy JSON</Button>
                        </Col>
                    )}
                    <Col xs="auto" style={{ marginLeft: 'auto' }}>
                        <FormGroup style={{ marginBottom: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Input
                                type="text"
                                placeholder="Query name..."
                                value={queryName}
                                onChange={(e) => this.setState({ queryName: e.target.value })}
                                style={{ width: '220px' }}
                            />
                            <Button color="success" onClick={this.handleSave}>
                                {currentQueryId ? 'Update' : 'New'}
                            </Button>
                            {currentQueryId && (
                                <Button
                                    color="secondary"
                                    onClick={() => this.setState({ currentQueryId: null, queryName: '' })}
                                >
                                    New
                                </Button>
                            )}
                        </FormGroup>
                    </Col>
                </Row>
                {this.renderSavedQueries()}
                {this.renderChart()}
                {this.renderResults()}
            </Container>
        )
    }
}

export default CustomSQLViewController
